import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FolderOpen, Search, Copy, Pencil, Trash2, Calendar, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useSavedScenarios } from '@/hooks/useSavedScenarios';
import type { ScenarioSetListItem } from '@/types/supremeIntelligenceTypes';

interface SavedScenariosManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadScenario?: (id: string) => void;
}

export const SavedScenariosManager: React.FC<SavedScenariosManagerProps> = ({
  open,
  onOpenChange,
  onLoadScenario,
}) => {
  const { savedScenarios, isLoading, deleteScenarios, duplicateScenarios, updateScenarios } = useSavedScenarios();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioSetListItem | null>(null);
  const [newName, setNewName] = useState('');

  const filteredScenarios = savedScenarios.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleDelete = async () => {
    if (!selectedScenario) return;

    try {
      await deleteScenarios(selectedScenario.id);
      toast.success('Scenario set deleted');
      setDeleteDialogOpen(false);
      setSelectedScenario(null);
    } catch (error) {
      toast.error('Failed to delete scenario set');
    }
  };

  const handleDuplicate = async (scenario: ScenarioSetListItem) => {
    try {
      await duplicateScenarios(scenario.id, `${scenario.name} (Copy)`);
      toast.success('Scenario set duplicated');
    } catch (error) {
      toast.error('Failed to duplicate scenario set');
    }
  };

  const handleRename = async () => {
    if (!selectedScenario || !newName.trim()) return;

    try {
      await updateScenarios(selectedScenario.id, { name: newName.trim() });
      toast.success('Scenario set renamed');
      setRenameDialogOpen(false);
      setSelectedScenario(null);
      setNewName('');
    } catch (error) {
      toast.error('Failed to rename scenario set');
    }
  };

  const handleLoad = (id: string) => {
    onLoadScenario?.(id);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Manage Saved Scenarios</DialogTitle>
            <DialogDescription>
              View, load, duplicate, rename, or delete your saved scenario sets
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search scenarios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Scenario List */}
            <ScrollArea className="h-[450px] pr-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : filteredScenarios.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[300px] text-center">
                  <FolderOpen className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No saved scenarios</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {searchQuery ? 'Try a different search term' : 'Save scenarios from the comparison view to see them here'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredScenarios.map((scenario) => (
                    <div
                      key={scenario.id}
                      className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <h4 className="font-medium">{scenario.name}</h4>
                          {scenario.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {scenario.description}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(scenario.updatedAt), 'MMM d, yyyy')}
                            </span>
                            <span>•</span>
                            <span>{scenario.scenarioCount} scenarios</span>
                            {scenario.tags && scenario.tags.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Tag className="w-3 h-3" />
                                  {scenario.tags.map((tag) => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {onLoadScenario && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleLoad(scenario.id)}
                            >
                              Load
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDuplicate(scenario)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedScenario(scenario);
                              setNewName(scenario.name);
                              setRenameDialogOpen(true);
                            }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedScenario(scenario);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scenario Set</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedScenario?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Rename Scenario Set</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter new name"
              maxLength={100}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={!newName.trim()}>
              Rename
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
